// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import {
  getFirestore, collection, doc, getDoc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, addDoc, serverTimestamp, getDocs
} from 'firebase/firestore';

const FCM_VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY
  || 'BFE1Pa4va663rmeBebHMlB_UUU1R3t-TkUAMq2I6jjxarW93PQpvCiVXeG55u606vzKnj7qMaJHse7gvG48YUgo';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);

// App Check — opt-in only (REACT_APP_ENABLE_APP_CHECK=true)
const appCheckEnabled = process.env.REACT_APP_ENABLE_APP_CHECK === 'true';
const recaptchaKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY;
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}
if (appCheckEnabled && recaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (appCheckEnabled && !recaptchaKey) {
  console.warn('REACT_APP_ENABLE_APP_CHECK is true but REACT_APP_RECAPTCHA_ENTERPRISE_KEY is missing.');
}

export const auth = getAuth(app);
export const db = getFirestore(app);

// ─── AUTH ─────────────────────────────────────
export const merchantLogin = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const merchantLogout = () => signOut(auth);
export const onAuthChange = (cb) => onAuthStateChanged(auth, cb);

// ─── GET MERCHANT SHOP ────────────────────────
export const getMerchantShop = async (uid) => {
  const snap = await getDocs(query(collection(db, 'shops'), where('merchantId', '==', uid)));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

// ─── LISTEN TO SHOP ORDERS ────────────────────
export const listenToShopOrders = (shopId, callback, onError) => {
  const q = query(collection(db, 'orders'), where('shopId', '==', shopId));
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter((o) => o.status !== 'payment_pending');
      orders.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      callback(orders);
    },
    (err) => {
      console.error('Shop orders listener:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

// ─── UPDATE ORDER STATUS ──────────────────────
export const updateOrderStatus = (orderId, status, extra = {}) => {
  const patch = { status, updatedAt: serverTimestamp(), ...extra };
  if (status === 'cancelled') patch.cancelledAt = serverTimestamp();
  if (status === 'confirmed') patch.confirmedAt = serverTimestamp();
  return updateDoc(doc(db, 'orders', orderId), patch);
};

// ─── LISTEN TO SHOP PRODUCTS ──────────────────
export const listenToShopProducts = (shopId, callback) => {
  const q = query(collection(db, 'products'), where('shopId', '==', shopId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

// ─── ADD PRODUCT ──────────────────────────────
export const addProduct = (data) =>
  addDoc(collection(db, 'products'), { ...data, createdAt: serverTimestamp() });

// ─── UPDATE PRODUCT ───────────────────────────
export const updateProduct = (id, data) =>
  updateDoc(doc(db, 'products', id), { ...data, updatedAt: serverTimestamp() });

// ─── DELETE PRODUCT ───────────────────────────
export const deleteProduct = (id) => deleteDoc(doc(db, 'products', id));

// ─── UPDATE SHOP ──────────────────────────────
export const updateShop = (shopId, data) =>
  updateDoc(doc(db, 'shops', shopId), { ...data, updatedAt: serverTimestamp() });

// ─── MERCHANT PAYOUTS (read-only) ─────────────
export const listenToMerchantPayouts = (shopId, callback, onError) => {
  const q = query(collection(db, 'payouts'), where('merchantId', '==', shopId));
  return onSnapshot(
    q,
    (snap) => {
      const payouts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      payouts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      callback(payouts);
    },
    (err) => {
      console.error('Merchant payouts listener:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

// ─── PLATFORM CONFIG (read-only) ──────────────
export const listenToPlatformConfig = (callback) => {
  return onSnapshot(doc(db, 'config', 'platform'), (snap) => {
    const data = snap.exists() ? snap.data() : {};
    callback({
      maxOrderTotal: data.maxOrderTotal ?? 1000,
    });
  });
};

// ─── FCM (merchant order alerts) ─────────────
export const registerMessagingSw = async () => {
  if (!('serviceWorker' in navigator)) return false;
  try {
    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    return true;
  } catch (e) {
    console.log('FCM SW:', e.message);
    return false;
  }
};

export const saveShopFcmToken = async (shopId) => {
  try {
    if (!(await isSupported())) return;
    await registerMessagingSw();
    const m = getMessaging(app);
    const token = await getToken(m, { vapidKey: FCM_VAPID_KEY });
    if (token) await updateShop(shopId, { fcmToken: token });
  } catch (e) {
    console.log('FCM:', e.message);
  }
};

export const listenToForegroundMessages = async (callback) => {
  if (!(await isSupported())) return () => {};
  const m = getMessaging(app);
  return onMessage(m, callback);
};
